import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  Description as DescriptionIcon,
  EditOutlined as EditOutlinedIcon,
  Search as SearchIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import InformeDinamicoWizard from './InformeDinamicoWizard';
import informesPersonalizadosService, {
  InformePersonalizadoAusenteError,
} from '../../services/informesPersonalizadosService';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  InformePersonalizadoDetalle,
  InformePersonalizadoListItem,
  ModuloInformePersonalizado,
  UsuarioEmpresaOpcion,
  statusHttp,
} from '../../utils/informePersonalizadoDefinicion';

interface InformesDefinicionesPanelProps {
  modulo: ModuloInformePersonalizado;
  onEditingChange?: (editing: boolean) => void;
  abrirAlta?: boolean;
  onAltaConsumida?: () => void;
}

type DefinicionesVista = 'detalle' | 'tarjetas' | 'iconos';

const VISTA_STORAGE_KEY = 'informesPersonalizados.definiciones.vista';

function isDefinicionesVista(value: string | null): value is DefinicionesVista {
  return value === 'detalle' || value === 'tarjetas' || value === 'iconos';
}

function readDefinicionesVista(): DefinicionesVista {
  try {
    const stored = localStorage.getItem(VISTA_STORAGE_KEY);
    return isDefinicionesVista(stored) ? stored : 'detalle';
  } catch {
    return 'detalle';
  }
}

const vistaButtonSx = (active: boolean) => ({
  color: active ? 'primary.main' : 'text.secondary',
  bgcolor: active ? 'primary.light' : 'transparent',
  '&:hover': { bgcolor: active ? 'primary.light' : 'action.hover' },
});

function formatFecha(value: string | null): string {
  if (!value) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function etiquetaTipo(tipo: InformePersonalizadoListItem['tipo']): string {
  return tipo === 'resumen' ? 'Resumen' : 'Detalle';
}

const InformesDefinicionesPanel: React.FC<InformesDefinicionesPanelProps> = ({
  modulo,
  onEditingChange,
  abrirAlta = false,
  onAltaConsumida,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<InformePersonalizadoListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detalle, setDetalle] = useState<InformePersonalizadoDetalle | null>(null);
  const [editando, setEditando] = useState(false);
  const [abriendoId, setAbriendoId] = useState<number | null>(null);
  const [usuariosEmpresa, setUsuariosEmpresa] = useState<UsuarioEmpresaOpcion[]>([]);
  const [usuariosEmpresaError, setUsuariosEmpresaError] = useState('');
  const [eliminar, setEliminar] = useState<InformePersonalizadoListItem | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [vista, setVista] = useState<DefinicionesVista>(readDefinicionesVista);
  const [searchTerm, setSearchTerm] = useState('');

  const itemsFiltrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const titulo = (item.titulo || '').toLowerCase();
      const tipo = etiquetaTipo(item.tipo).toLowerCase();
      return titulo.includes(q) || tipo.includes(q);
    });
  }, [items, searchTerm]);

  const cambiarVista = (next: DefinicionesVista) => {
    setVista(next);
    try {
      localStorage.setItem(VISTA_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await informesPersonalizadosService.list(modulo));
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, 'No se pudieron cargar los informes personalizados.');
      setError(message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [modulo]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => {
    let activo = true;
    informesPersonalizadosService.listUsuariosEmpresa()
      .then((usuarios) => {
        if (!activo) return;
        setUsuariosEmpresa(usuarios);
        setUsuariosEmpresaError('');
      })
      .catch((requestError) => {
        if (!activo) return;
        setUsuariosEmpresa([]);
        setUsuariosEmpresaError(getApiErrorMessage(
          requestError,
          'No se pudo cargar el listado de usuarios de la empresa.',
        ));
      });
    return () => {
      activo = false;
    };
  }, []);

  const abrirWizard = (next: InformePersonalizadoDetalle | null) => {
    setDetalle(next);
    setEditando(true);
    onEditingChange?.(true);
  };

  useEffect(() => {
    if (!abrirAlta) return;
    abrirWizard(null);
    onAltaConsumida?.();
  }, [abrirAlta]);

  const volverAlListado = () => {
    setEditando(false);
    setDetalle(null);
    onEditingChange?.(false);
    void cargar();
  };

  const abrir = async (item: InformePersonalizadoListItem) => {
    setAbriendoId(item.id);
    try {
      const data = await informesPersonalizadosService.get(item.id, modulo);
      abrirWizard(data);
    } catch (requestError) {
      if (requestError instanceof InformePersonalizadoAusenteError || statusHttp(requestError) === 404) {
        enqueueSnackbar('El informe no está en tu listado.', { variant: 'warning' });
        void cargar();
      } else {
        enqueueSnackbar(getApiErrorMessage(requestError, 'No se pudo abrir el informe.'), { variant: 'error' });
      }
    } finally {
      setAbriendoId(null);
    }
  };

  const confirmarEliminacion = async () => {
    if (!eliminar) return;
    setEliminando(true);
    try {
      await informesPersonalizadosService.remove(eliminar.id);
      enqueueSnackbar('Informe eliminado.', { variant: 'success' });
      setEliminar(null);
      await cargar();
    } catch (requestError) {
      if (statusHttp(requestError) === 404) {
        enqueueSnackbar('El informe no está en tu listado.', { variant: 'warning' });
        setEliminar(null);
        await cargar();
      } else if (statusHttp(requestError) === 403) {
        enqueueSnackbar(getApiErrorMessage(requestError, 'No tenés permiso para eliminar este informe.'), { variant: 'error' });
      } else {
        enqueueSnackbar(getApiErrorMessage(requestError, 'No se pudo eliminar el informe.'), { variant: 'error' });
      }
    } finally {
      setEliminando(false);
    }
  };

  const renderAcciones = (item: InformePersonalizadoListItem) => (
    <>
      <Tooltip title={item.acceso === 'consulta' ? 'Consultar' : 'Abrir'}>
        <span>
          <IconButton
            color="primary"
            disabled={abriendoId === item.id}
            onClick={() => void abrir(item)}
            aria-label={item.acceso === 'consulta' ? 'Consultar informe' : 'Abrir informe'}
          >
            {abriendoId === item.id ? (
              <CircularProgress size={18} />
            ) : item.acceso === 'consulta' ? (
              <VisibilityOutlinedIcon />
            ) : (
              <EditOutlinedIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {item.acceso !== 'consulta' && (
        <Tooltip title="Eliminar">
          <IconButton
            color="error"
            aria-label="Eliminar informe"
            onClick={() => setEliminar(item)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const renderVistaLista = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Título</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Desde</TableCell>
            <TableCell>Hasta</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itemsFiltrados.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>{item.titulo || 'Sin título'}</TableCell>
              <TableCell>{etiquetaTipo(item.tipo)}</TableCell>
              <TableCell>{formatFecha(item.fechaDesde)}</TableCell>
              <TableCell>{formatFecha(item.fechaHasta)}</TableCell>
              <TableCell align="right">{renderAcciones(item)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderVistaTarjetas = () => (
    <Grid container spacing={2}>
      {itemsFiltrados.map((item) => (
        <Grid item xs={12} sm={6} md={3} key={item.id}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {item.titulo || 'Sin título'}
                  </Typography>
                  <Chip size="small" label={etiquetaTipo(item.tipo)} variant="outlined" />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Desde {formatFecha(item.fechaDesde)} · Hasta {formatFecha(item.fechaHasta)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{renderAcciones(item)}</Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderVistaIconos = () => (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
          xl: 'repeat(6, minmax(0, 1fr))',
        },
      }}
    >
      {itemsFiltrados.map((item) => (
        <Card variant="outlined" key={item.id} sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Stack spacing={1} alignItems="center">
              <DescriptionIcon sx={{ fontSize: 48, color: '#1976d2' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={item.titulo || 'Sin título'}>
                {item.titulo || 'Sin título'}
              </Typography>
              <Chip size="small" label={etiquetaTipo(item.tipo)} variant="outlined" />
              <Box>{renderAcciones(item)}</Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  if (editando) {
    return (
      <InformeDinamicoWizard
        modulo={modulo}
        definicion={detalle}
        acceso={detalle?.acceso ?? 'propietario'}
        usuariosEmpresa={usuariosEmpresa}
        usuariosEmpresaError={usuariosEmpresaError}
        onBack={volverAlListado}
        onSaved={() => { void cargar(); }}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
      >
        <Typography variant="body2" color="text.secondary">
          La vista previa y el Excel salen del detalle de {modulo === 'Contabilidad' ? 'contabilidad' : modulo === 'Compras' ? 'compras' : modulo === 'Tesoreria' ? 'tesorería' : 'ventas'} que ya existe.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => abrirWizard(null)}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 500 }}
        >
          Nuevo informe
        </Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <TextField
          fullWidth
          placeholder="Buscar por nombre o tipo..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#1976d2' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
        />
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 0.5,
            alignSelf: { xs: 'flex-end', sm: 'center' },
            flexShrink: 0,
          }}
        >
          <IconButton
            size="small"
            onClick={() => cambiarVista('detalle')}
            sx={vistaButtonSx(vista === 'detalle')}
            title="Vista lista"
            aria-label="Vista lista"
          >
            <ViewListIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => cambiarVista('tarjetas')}
            sx={vistaButtonSx(vista === 'tarjetas')}
            title="Vista tarjetas"
            aria-label="Vista tarjetas"
          >
            <ViewAgendaIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => cambiarVista('iconos')}
            sx={vistaButtonSx(vista === 'iconos')}
            title="Vista iconos"
            aria-label="Vista iconos"
          >
            <ViewModuleIcon />
          </IconButton>
        </Box>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          Todavía no hay informes personalizados en este módulo.
        </Typography>
      ) : itemsFiltrados.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No hay informes que coincidan con la búsqueda.
        </Typography>
      ) : vista === 'iconos' ? (
        renderVistaIconos()
      ) : vista === 'tarjetas' ? (
        renderVistaTarjetas()
      ) : (
        renderVistaLista()
      )}

      <Dialog open={Boolean(eliminar)} onClose={() => { if (!eliminando) setEliminar(null); }}>
        <DialogTitle>Eliminar informe</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se va a eliminar «{eliminar?.titulo || 'este informe'}». Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminar(null)} disabled={eliminando} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => void confirmarEliminacion()} disabled={eliminando} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {eliminando ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default InformesDefinicionesPanel;
